import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  Globe,
  Home,
  LogOut,
  Menu,
  Moon,
  Settings,
  Sun,
  Users,
} from "lucide-react";
import React from "react";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components";
import { useAuth, usePartnerBranding } from "@/context/auth";
import { useI18n } from "@/context/i18n";
import { useSidebar } from "@/context/sidebar";
import { useTheme } from "@/context/theme";
import { cn } from "@/lib/utils";
import { getClientById } from "@/pages/clients/clients.api";

export const Header: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { setLocale } = useI18n();
  const { toggle } = useSidebar();
  const [mounted, setMounted] = React.useState(false);
  const { auth, logout } = useAuth();
  const { logoUrl, brandName } = usePartnerBranding();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  // Detecta qualquer rota dentro de /clients/:clientId/... (exceto /clients/select/*)
  const clientMatch = useMatch("/clients/:clientId/*");
  const clientId = clientMatch?.params?.clientId;
  const isPartnerRoute = Boolean(clientId && clientId !== "select");

  const { data: partnerData } = useQuery({
    queryKey: ["client-header", clientId],
    queryFn: () => getClientById(clientId as string),
    enabled: isPartnerRoute,
    staleTime: 5 * 60 * 1000,
  });

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  const sidebarItems = [
    {
      name: "dashboard.home",
      href: "/dashboard",
      icon: Home,
    },
    // {
    //   name: "dashboard.clients",
    //   href: "/dashboard/clients",
    //   icon: Bot,
    // },
  ];

  return (
    <header className="sticky top-0 h-20 z-30 flex items-center gap-4 bg-background px-4 md:px-6 shadow-sm dark:border-b dark:border-b-muted/55">
      <nav className="w-full flex flex-row gap-2 text-lg font-medium items-center md:gap-5 md:text-sm lg:gap-6 justify-center">
        <div className="flex flex-row w-full mx-auto">
          <div className="flex items-center gap-4 w-58 ">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={toggle}
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">{t("toggle.menu")}</span>
            </Button>
            <Link
              to="/dashboard"
              className="items-center space-x-2 hidden md:flex"
            >
              <img
                src={logoUrl}
                alt={brandName}
                className="max-h-14 max-w-[160px] object-contain"
              />
            </Link>
          </div>
          {partnerData && (
            <div className="flex items-center gap-2 ml-4 px-3 py-1.5 rounded-md bg-muted/60 border border-border/60 min-w-0 max-w-xs">
              <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium truncate text-foreground">
                {partnerData.name}
              </span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <div className="flex flex-row items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors">
              {sidebarItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex flex-row items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors mb-1 hover:bg-muted"
                >
                  <item.icon
                    className={cn("h-5 w-5", {
                      "text-menu-item": location.pathname === item.href,
                    })}
                  />
                  <span>{t(item.name)}</span>
                </Link>
              ))}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Globe className="h-5 w-5" />
                  <span className="sr-only">{t("toggle.language")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLocale("en-US")}>
                  {t("languages.english")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLocale("pt-BR")}>
                  {t("languages.portuguese")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
              <span className="sr-only">Toggle theme</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative flex items-center gap-2 h-8 rounded-full px-2"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      className="object-cover h-full w-full"
                      src={auth.userPicture}
                      alt={auth.name}
                    />
                    <AvatarFallback>
                      {auth.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuItem className="hover:bg-transparent">
                  <div className="flex flex-col border-b-2 border-b-muted/55 pb-2 font-medium w-full">
                    <p className="text-sm">{auth.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {auth.email}
                    </p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                >
                  <div className="flex flex-row gap-4 w-full">
                    <Settings
                      className={cn("h-4 w-4", {
                        "text-menu-item":
                          location.pathname === "/settings",
                      })}
                    />
                    <span>{t("dashboard.settings")}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-4"
                  onClick={logout}
                >
                  <LogOut className="h-4 w-4" />
                  {t("generic.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>
    </header>
  );
};
